import type { Control, FieldValues } from 'react-hook-form';
import type { OdinDataRetrieval } from '../../../common/data-retrieval/data-retrieval-interfaces';
import OdinController from '../../dynamic-form/odin-controller.component';
import { useResolveDynamicFormData } from '../../dynamic-form/use-resolve-dynamic-form-data';
import type { JsonData } from '../../generic-types';
import type { CheckBoxGroupProps } from './checkbox-group.component';
import CheckboxGroup from './checkbox-group.component';

export interface HfCheckBoxGroupProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<CheckBoxGroupProps, 'data'> {
  control: Control<TFieldValues>;
  name: string;
  /**
   * Static array of options, OR an `OdinDataRetrieval` for fields whose data is injected by
   * `DynamicForm`'s `dataRetrieval` prop. Resolved to a static array before being passed to the
   * underlying `CheckboxGroup`.
   */
  data?: JsonData | OdinDataRetrieval;
  multiSelectCheckboxType?: 'buttons' | 'checkboxes';
}

export default function HfCheckboxGroup<TFieldValues extends FieldValues = FieldValues>({
  id,
  control,
  name,
  label,
  data,
  value,
  textField,
  valueField,
  readOnly,
  required,
  onChange,
  fieldValueConverter,
  disabled,
  className,
  multiSelectCheckboxType = 'checkboxes',
  labelStyles,
  checkboxGroupStyling,
  archivedField,
  showSelectedArchivedOptions,
}: HfCheckBoxGroupProps<TFieldValues>) {
  const resolvedData = useResolveDynamicFormData(data, id, 'MULTISELECTCHECKBOX');

  return (
    <OdinController
      name={name}
      control={control}
      defaultValue={value}
      onChange={onChange}
      fieldValueConverter={fieldValueConverter}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <CheckboxGroup
          id={id}
          name={name}
          label={label}
          error={error?.message}
          value={value}
          onChange={onChange}
          textField={textField}
          valueField={valueField}
          data={resolvedData}
          readOnly={readOnly}
          required={required}
          disabled={disabled}
          className={className}
          multiSelectCheckboxType={multiSelectCheckboxType}
          labelStyles={labelStyles}
          checkboxGroupStyling={checkboxGroupStyling}
          archivedField={archivedField}
          showSelectedArchivedOptions={showSelectedArchivedOptions}
        />
      )}
    />
  );
}
